import {useState, useCallback} from 'react'

/**
 * Generic editor state machine.
 * Supports overview, field editing, final review, and custom flow-specific states.
 *
 * @example
 * ```typescript
 * // Basic usage (OrderIntake - no custom states)
 * const {state, goToOverview, goToField} = useEditorState()
 *
 * // OrderManagement with finalReview
 * const {state, goToFinalReview} = useEditorState()
 *
 * // With additional custom states
 * const {state, goToState} = useEditorState<'cancellation'>()
 * goToState('cancellation')
 * ```
 */
export type EditorState<TCustomState extends string = never> =
  | {type: 'overview'}
  | {type: 'field'; fieldId: string}
  | {type: 'finalReview'}
  | {type: TCustomState}

/**
 * Hook to manage editor navigation state.
 * Handles state transitions and provides type-safe navigation methods.
 *
 * @param initialState - Initial state (defaults to overview)
 * @returns State management object with navigation methods
 */
export const useEditorState = <TCustomState extends string = never>(
  initialState: EditorState<TCustomState> = {type: 'overview'} as EditorState<TCustomState>
) => {
  const [state, setState] = useState<EditorState<TCustomState>>(initialState)

  /**
   * Navigate to overview state
   */
  const goToOverview = useCallback(() => {
    setState({type: 'overview'} as EditorState<TCustomState>)
  }, [])

  /**
   * Navigate to field editing state
   * @param fieldId - The field to edit
   */
  const goToField = useCallback((fieldId: string) => {
    setState({type: 'field', fieldId} as EditorState<TCustomState>)
  }, [])

  /**
   * Navigate to final review state
   */
  const goToFinalReview = useCallback(() => {
    setState({type: 'finalReview'} as EditorState<TCustomState>)
  }, [])

  /**
   * Navigate to custom state (only available if TCustomState is defined)
   * @param customState - The custom state type
   * @param data - Additional data for the custom state
   */
  const goToState = useCallback(<T extends TCustomState>(state: T) => {
    setState({type: state} as EditorState<TCustomState>)
  }, [])

  /**
   * Check if currently in overview state
   */
  const isOverview = state.type === 'overview'

  /**
   * Check if currently in field editing state
   */
  const isField = state.type === 'field'

  /**
   * Check if currently in final review state
   */
  const isFinalReview = state.type === 'finalReview'

  /**
   * Get current field ID (null if not in field state)
   */
  const currentFieldId = isField && 'fieldId' in state ? state.fieldId : null

  return {
    state,
    setState,
    goToOverview,
    goToField,
    goToFinalReview,
    goToState,
    isOverview,
    isField,
    isFinalReview,
    currentFieldId
  }
}
